import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { Trophy, Vote, Users, Crown, Star, CheckCircle, Clock, Square, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const VotingPanel = () => {
  const { token, user } = useContext(AuthContext);
  const [poll, setPoll] = useState(null);
  const [results, setResults] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [nomineeInput, setNomineeInput] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);

  // Fetch current poll
  useEffect(() => {
    const fetchPoll = async () => {
      try {
        const response = await axios.get(`${API}/voting/current-poll`);
        if (response.data.poll) {
          setPoll(response.data.poll);
          // Fetch results
          const resultsRes = await axios.get(`${API}/voting/results/${response.data.poll.poll_id}`);
          setResults(resultsRes.data.results || []);
          setTotalVotes(resultsRes.data.total_votes || 0);
        }
      } catch (error) {
        console.error("Failed to fetch poll:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPoll();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPoll, 60000);
    return () => clearInterval(interval);
  }, []);

  const castVote = async () => {
    const pollType = poll?.poll_type || "text";
    
    if (pollType === "text" && !nomineeInput.trim()) {
      toast.error("Enter a username to vote for");
      return;
    }
    if ((pollType === "single_choice" || pollType === "multiple_choice") && selectedOptions.length === 0) {
      toast.error("Select at least one option");
      return;
    }
    if (pollType === "single_choice" && selectedOptions.length > 1) {
      toast.error("Select only one option");
      return;
    }
    if (!token) {
      toast.error("Please log in to vote");
      return;
    }
    
    setVoting(true);
    try {
      const payload = { poll_id: poll.poll_id };
      if (pollType === "text") {
        payload.nominee_username = nomineeInput.trim();
      } else {
        payload.selected_options = selectedOptions;
      }
      
      await axios.post(`${API}/voting/cast-vote`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Vote cast successfully!");
      setHasVoted(true);
      setNomineeInput("");
      setSelectedOptions([]);
      
      // Refresh results
      const resultsRes = await axios.get(`${API}/voting/results/${poll.poll_id}`);
      setResults(resultsRes.data.results || []);
      setTotalVotes(resultsRes.data.total_votes || 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cast vote");
    } finally {
      setVoting(false);
    }
  };

  const toggleOption = (option) => {
    const pollType = poll?.poll_type || "text";
    
    if (pollType === "single_choice") {
      setSelectedOptions([option]);
    } else {
      setSelectedOptions(prev => 
        prev.includes(option) 
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    }
  };

  if (loading) {
    return (
      <div className="retro-window mb-3" data-testid="voting-panel">
        <div className="retro-title-bar px-2 py-1">
          <span className="flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            🏆 VOTING
          </span>
        </div>
        <div className="p-4 text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="retro-window mb-3" data-testid="voting-panel">
        <div className="retro-title-bar px-2 py-1">
          <span className="flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            🏆 VOTING
          </span>
        </div>
        <div className="p-4 text-center text-gray-500 text-sm">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No active voting poll
        </div>
      </div>
    );
  }

  const pollType = poll.poll_type || "text";

  return (
    <div className="retro-window mb-3" data-testid="voting-panel">
      <div className="bg-gradient-to-r from-yellow-600 via-amber-500 to-yellow-600 text-white px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            <span className="font-bold">{poll.title}</span>
          </div>
          <button
            onClick={() => setShowResults(!showResults)}
            className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
          >
            {showResults ? "Hide Results" : "Show Results"}
          </button>
        </div>
        {poll.description && (
          <p className="text-xs opacity-80 mt-1">{poll.description}</p>
        )}
        <p className="text-[10px] opacity-60 mt-1">
          {pollType === "text" && "📝 Type a username to vote"}
          {pollType === "single_choice" && "🔘 Select one option"}
          {pollType === "multiple_choice" && "☑️ Select multiple options"}
        </p>
      </div>

      <div className="p-3 bg-gradient-to-b from-amber-50 to-yellow-50">
        {/* Results Section */}
        {showResults && results.length > 0 && (
          <div className="mb-3 bg-white rounded-lg p-2 border border-amber-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-700 flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500" />
                Current Leaders
              </span>
              <span className="text-xs text-gray-500">{totalVotes} total votes</span>
            </div>
            <div className="space-y-1">
              {results.slice(0, 5).map((result, index) => (
                <div 
                  key={result.username || result.option}
                  className={`flex items-center justify-between p-1.5 rounded ${
                    index === 0 ? 'bg-yellow-100 border border-yellow-400' :
                    index === 1 ? 'bg-gray-100' :
                    index === 2 ? 'bg-orange-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`font-bold text-sm ${
                      index === 0 ? 'text-yellow-600' :
                      index === 1 ? 'text-gray-500' :
                      index === 2 ? 'text-orange-500' : 'text-gray-600'
                    }`}>
                      #{index + 1}
                    </span>
                    {index === 0 && <Crown className="w-4 h-4 text-yellow-500" />}
                    <span className="font-medium text-gray-800">{result.username || result.option}</span>
                  </div>
                  <span className="text-sm font-bold text-amber-600">{result.votes} votes</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vote Input */}
        {!hasVoted ? (
          <div className="space-y-2">
            {pollType === "text" ? (
              <>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700 font-medium">Vote for a user:</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nomineeInput}
                    onChange={(e) => setNomineeInput(e.target.value)}
                    placeholder="Enter username..."
                    className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    onKeyDown={(e) => e.key === 'Enter' && castVote()}
                  />
                  <button
                    onClick={castVote}
                    disabled={voting || !nomineeInput.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold rounded-lg hover:from-yellow-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                  >
                    <Vote className="w-4 h-4" />
                    {voting ? "..." : "Vote"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  {pollType === "single_choice" ? (
                    <span className="text-sm text-gray-700 font-medium">🔘 Select one option:</span>
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">☑️ Select options:</span>
                  )}
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {poll.options?.map((option, index) => (
                    <div
                      key={index}
                      onClick={() => toggleOption(option)}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 border transition-colors ${
                        selectedOptions.includes(option)
                          ? 'bg-amber-100 border-amber-400'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {pollType === "single_choice" ? (
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          selectedOptions.includes(option) ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                        }`}>
                          {selectedOptions.includes(option) && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                      ) : (
                        selectedOptions.includes(option) ? (
                          <CheckSquare className="w-4 h-4 text-amber-500" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-300" />
                        )
                      )}
                      <span className="text-sm">{option}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={castVote}
                  disabled={voting || selectedOptions.length === 0}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold rounded-lg hover:from-yellow-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-sm mt-2"
                >
                  <Vote className="w-4 h-4" />
                  {voting ? "Submitting..." : `Submit Vote${pollType === "multiple_choice" && selectedOptions.length > 0 ? ` (${selectedOptions.length})` : ''}`}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-2">
            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-1" />
            <p className="text-green-600 font-medium text-sm">Thanks for voting!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VotingPanel;
